/**
 * Returns the appropriate icon and color for a power source based on battery bars and type.
 */
export function powerSourceIcon(batteryBars, batteryType = null) {
    if (batteryType === 3) {
        return { icon: 'mdi-power-plug', color: 'green' };
    }
    if (batteryType === 4) {
        return { icon: 'mdi-flash', color: 'amber' };
    }
    return batteryIcon(batteryBars);
}

export function batteryIcon(batteryBars) {
    const bars = batteryBars ?? 0;
    if (bars <= 0) return { icon: 'mdi-battery-off-outline', color: 'red' };
    if (bars === 1) return { icon: 'mdi-battery-low', color: 'red' };
    if (bars === 2) return { icon: 'mdi-battery-medium', color: 'amber' };
    return { icon: 'mdi-battery-high', color: 'green' };
}

/**
 * True ONLY for a phone-as-tracker device, per the explicit `is_phone` flag.
 * Do NOT fall back to "has battery_percent": hardware vehicles now also carry a
 * battery_percent (from a configured voltage range), so that fallback would
 * misclassify a vehicle as a phone and trigger the phone voltage1/50 decode on
 * it. This must stay strict so the phone-only MQTT decode never runs on hardware.
 */
export function isPhoneDevice(device) {
    return device?.is_phone === true;
}

/** True when the device reports a battery percent (a phone, or a hardware
 * vehicle with a voltage range configured). Drives the "%" display; kept
 * separate from isPhoneDevice so showing a percent never implies "is a phone". */
export function hasBatteryPercent(device) {
    return device?.battery_percent !== null && device?.battery_percent !== undefined;
}

/**
 * Live percent for a hardware vehicle with a configured voltage range, from a
 * fresh MQTT sample. Mirrors the API (DeviceLiveViewResource::resolveBatteryPercent
 * -> Asset::batteryPercentFromVoltage): the SKU's battery1_status / battery2_status
 * pick the primary channel (battery1 first, like the server), the asset's
 * [battery_low_voltage, battery_high_voltage] window (volts) maps it linearly,
 * clamped 0-100. Only the asset-range case is recomputed here: the server also
 * falls back to SKU thresholds the client does not have, so without a valid
 * asset window this returns undefined and the caller keeps the API value.
 * Voltages are millivolts on both the REST row and the MQTT payload.
 */
export function vehicleBatteryPercent(device, voltage1Mv, voltage2Mv) {
    if (isPhoneDevice(device)) return undefined;
    if (device?.battery_voltage_preset === null || device?.battery_voltage_preset === undefined) return undefined;
    // Number(null) is 0, which would silently widen the window to 0-high.
    if (device?.battery_low_voltage == null || device?.battery_high_voltage == null) return undefined;
    const low = Number(device.battery_low_voltage);
    const high = Number(device.battery_high_voltage);
    if (!Number.isFinite(low) || !Number.isFinite(high) || low >= high) return undefined;
    const raw = device?.battery1_status ? voltage1Mv : (device?.battery2_status ? voltage2Mv : null);
    const mv = Number(raw);
    if (raw === null || raw === undefined || !Number.isFinite(mv)) return undefined;
    // toPrecision(15): PHP's round() (8.1, prod) pre-rounds to 15 significant
    // digits, so a near-tie like 14.499999999999957 is 15 there; match it so the
    // live value never disagrees with the next REST refresh by a point.
    const pct = Number(((mv / 1000 - low) / (high - low) * 100).toPrecision(15));
    return Math.max(0, Math.min(100, Math.round(pct)));
}

/**
 * Unified battery display for a device. Phones report an exact percent
 * (`battery_percent`, from the API for the phone SKU), so we show "58%" with a
 * matching icon/color. Hardware has no meaningful percent, so it falls back to
 * the existing bars icon (+ voltage text is shown separately by the caller).
 * Color thresholds match the phone-SKU alert bands: <=20% critical, <=40% low.
 *
 * Returns { isPercent, percent, label, icon, color }.
 */
export function batteryDisplay(device) {
    // Show a percent for a phone OR a hardware vehicle with a configured range.
    if (isPhoneDevice(device) || hasBatteryPercent(device)) {
        const pct = device?.battery_percent;
        // Reports percent but hasn't sent one yet (e.g. a phone pre-first-reading):
        // show a neutral unknown, not a misleading 0% and not the hardware bars.
        if (pct === null || pct === undefined) {
            return { isPercent: true, percent: null, label: '--', icon: 'mdi-battery-unknown', color: 'grey' };
        }
        const p = Math.max(0, Math.min(100, Number(pct)));
        const icon = p <= 20 ? 'mdi-battery-low'
            : p <= 40 ? 'mdi-battery-medium'
                : 'mdi-battery-high';
        const color = p <= 20 ? 'red' : p <= 40 ? 'amber' : 'green';
        return { isPercent: true, percent: p, label: `${p}%`, icon, color };
    }
    const { icon, color } = powerSourceIcon(device?.battery_bars ?? 0, selectedBatteryType(device));
    return { isPercent: false, percent: null, label: null, icon, color };
}

/**
 * Type of the SKU's SELECTED battery channel, for the power-source icon.
 * battery1_status / battery2_status pick which channel a SKU reports on
 * (channel 1 wins); the other channel's type is not the user-facing power
 * source. `battery1_type ?? battery2_type` took channel 1's SuperCap (4) on
 * every OBD/Fleet SKU whose selected channel 2 is external power (3), drawing
 * the amber flash instead of the green plug. Rows without flags keep the old
 * first-non-null fallback.
 */
export function selectedBatteryType(device) {
    if (!device) return null;
    if (device.battery1_status === true) return device.battery1_type ?? null;
    if (device.battery2_status === true) return device.battery2_type ?? null;
    return device.battery1_type ?? device.battery2_type ?? null;
}

/**
 * Battery level as text for a table cell: "58%" for a phone or a ranged
 * vehicle, otherwise the SKU-selected `voltage` (millivolts) as "12.51v".
 * Mirrors DevicesMap's popup so the table view and the map agree.
 */
export function batteryLevelText(device) {
    const b = batteryDisplay(device);
    if (b.isPercent) return b.label;
    const mv = parseFloat(device?.voltage);
    return Number.isNaN(mv) ? '' : `${(mv / 1000).toFixed(2)}v`;
}

/**
 * Battery reading for a trip route point, selected and labelled by the
 * device's SKU channel flags (same precedence as the alert renderer): the
 * primary channel first under its own label, the other channel second when it
 * carries a reading. Phones encode a percent on voltage1. Returns null when the
 * device carries no flags, so the caller keeps its generic output.
 *
 * @returns {{label: string, text: string, secondary: {label: string, text: string}|null}|null}
 */
export function routePointBattery(device, voltage1Mv, voltage2Mv) {
    if (isPhoneDevice(device)) {
        const mv = Number(voltage1Mv);
        const pct = mv > 0 ? `${Math.max(0, Math.min(100, Math.round(mv / 50)))}%` : '--';
        return { label: device?.battery1_label || 'Battery', text: pct, secondary: null };
    }
    const usesBat1 = device?.battery1_status === true;
    const usesBat2 = !usesBat1 && device?.battery2_status === true;
    if (!usesBat1 && !usesBat2) return null;
    const primaryMv = usesBat2 ? voltage2Mv : voltage1Mv;
    const otherMv = usesBat2 ? voltage1Mv : voltage2Mv;
    const label = (usesBat2 ? device.battery2_label : device.battery1_label) || 'Battery Voltage';
    const otherLabel = (usesBat2 ? device.battery1_label : device.battery2_label) || 'Other Channel';
    const volts = (mv) => `${(Number(mv) / 1000).toFixed(2)}v`;
    return {
        label,
        text: volts(primaryMv),
        secondary: Number(otherMv) > 0 ? { label: otherLabel, text: volts(otherMv) } : null,
    };
}

/**
 * True when a device's battery is at/below the low threshold and warrants a
 * warning affordance. Unifies the previously-inconsistent per-surface thresholds
 * to a single 20% for percent devices (phones + ranged vehicles) / < 2 bars for
 * hardware without a configured voltage range. Returns false when the level is
 * unknown (pre-first-reading), so we never nag on missing data.
 *
 * Note: do NOT gate on battery1_type/battery2_type - those are static SKU power
 * classes (3 = ExternalPower, 4 = SuperCapacitor), not a live charging state, and
 * a ranged vehicle reporting a percent is wired to external power (type 3), so a
 * type gate would hide the low-battery icon for the exact devices U2/U12 target.
 */
export function isLowBatteryDevice(device) {
    const disp = batteryDisplay(device);
    if (disp.isPercent) {
        return disp.percent !== null && disp.percent !== undefined && disp.percent <= 20;
    }
    const bars = device?.battery_bars;
    return bars !== null && bars !== undefined && Number(bars) < 2;
}

/**
 * Tooltip/label for the low-battery affordance. Phones get a "recharge your
 * phone" framing (so a viewer understands why a phone may stop reporting);
 * vehicles get a neutral "Low battery".
 */
export function lowBatteryHint(device) {
    return isPhoneDevice(device) ? 'Low battery - recharge your phone' : 'Low battery';
}
